const HUBSPOT_SCRIPT_ID = "hubspot-forms-embed-script";
const HUBSPOT_SCRIPT_SRC = "https://js-eu1.hsforms.net/forms/embed/v2.js";

type HubSpotFormsCreate = (options: {
  region: string;
  portalId: string;
  formId: string;
  target: string;
  css?: string;
}) => void;

type HubSpotWindow = Window & {
  hbspt?: {
    forms?: {
      create: HubSpotFormsCreate;
    };
  };
};

// Promise-based loader — guarantees the script loads once and resolves when hbspt.forms is ready
let hubspotReady: Promise<void> | null = null;

const loadHubSpot = (): Promise<void> => {
  if (hubspotReady) return hubspotReady;

  hubspotReady = new Promise<void>((resolve, reject) => {
    const w = window as HubSpotWindow;

    // Already loaded
    if (w.hbspt?.forms) {
      resolve();
      return;
    }

    const existing = document.getElementById(HUBSPOT_SCRIPT_ID) as HTMLScriptElement | null;

    if (!existing) {
      const script = document.createElement("script");
      script.id = HUBSPOT_SCRIPT_ID;
      script.src = HUBSPOT_SCRIPT_SRC;
      script.charset = "utf-8";
      script.addEventListener("error", () => reject(new Error("HubSpot script failed to load")), { once: true });
      document.body.appendChild(script);
    }

    // Poll for hbspt.forms to become available
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (w.hbspt?.forms) {
        clearInterval(interval);
        resolve();
      } else if (attempts > 100) {
        clearInterval(interval);
        reject(new Error("HubSpot script timed out"));
      }
    }, 100);
  });

  // Allow retry on failure
  hubspotReady.catch(() => {
    hubspotReady = null;
  });

  return hubspotReady;
};

/**
 * Ensures the HubSpot script is loaded, then calls the callback.
 * Safe to call multiple times — the script is only loaded once.
 */
export const ensureHubSpotScript = (callback: () => void) => {
  loadHubSpot().then(callback).catch((err) => {
    console.warn("[HubSpot]", err.message);
  });
};

export const createHubSpotForm = (options: {
  region: string;
  portalId: string;
  formId: string;
  target: string;
  css?: string;
}) => {
  const w = window as HubSpotWindow;
  w.hbspt?.forms?.create(options);

  // Inject CSS into the HubSpot iframe after it loads
  if (options.css) {
    const injectCss = () => {
      const container = document.querySelector(options.target);
      if (!container) return;

      const iframe = container.querySelector("iframe") ||
        container.parentElement?.querySelector("iframe");
      if (!iframe) return;

      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) return;

        // Check if already injected
        if (iframeDoc.getElementById("hs-custom-css")) return;

        const style = iframeDoc.createElement("style");
        style.id = "hs-custom-css";
        style.textContent = options.css!;
        iframeDoc.head.appendChild(style);
        return true;
      } catch {
        // Cross-origin iframe — CSS injection not possible, form still works
        return false;
      }
    };

    // Poll for the iframe to appear and be accessible
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (injectCss() || attempts > 50) {
        clearInterval(interval);
      }
    }, 200);
  }
};

/* Thank-you / success message styling — dark blue box with white text */
const HUBSPOT_THANK_YOU_CSS = `
  .submitted-message, .hs-form__thank-you, .hs_cos_wrapper_type_rich_text.submitted-message {
    background-color: #0E2C43 !important;
    color: #ffffff !important;
    border: 1px solid #1679B1 !important;
    border-radius: 0.5rem !important;
    padding: 1.5rem !important;
    font-family: 'Montserrat', sans-serif !important;
    font-size: 1rem !important;
    line-height: 1.6 !important;
  }
  .submitted-message *, .hs-form__thank-you * {
    color: #ffffff !important;
  }
  .submitted-message a, .hs-form__thank-you a {
    color: #7FC4EA !important;
    text-decoration: underline !important;
  }
`;

/* Shared CSS for dark-themed HubSpot forms (modals, dark sections) */
export const HUBSPOT_DARK_CSS = `

  body { background: transparent !important; }
  label, .hs-field-desc, legend {
    color: #e2e8f0 !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    font-family: 'Montserrat', sans-serif !important;
    margin-bottom: 0.375rem !important;
  }
  .hs-input, input[type="text"], input[type="email"], input[type="url"],
  input[type="tel"], input[type="number"], textarea, select {
    background-color: hsl(212 25% 18%) !important;
    border: 1px solid hsl(215 15% 28%) !important;
    border-radius: 0.375rem !important;
    padding: 0.625rem 1rem !important;
    font-size: 0.875rem !important;
    color: #e2e8f0 !important;
    font-family: 'Montserrat', sans-serif !important;
    width: 100% !important;
  }
  .hs-input:focus, input:focus, textarea:focus, select:focus {
    outline: none !important;
    box-shadow: 0 0 0 2px hsl(207 82% 47% / 0.5) !important;
    border-color: hsl(207 82% 47%) !important;
  }
  .hs-input::placeholder, input::placeholder, textarea::placeholder {
    color: hsl(215 15% 60%) !important;
  }
  .hs-button, input[type="submit"] {
    background-color: hsl(207 82% 47%) !important;
    color: #fff !important;
    border: none !important;
    border-radius: 0.375rem !important;
    padding: 0.75rem 1.5rem !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    font-family: 'Montserrat', sans-serif !important;
    cursor: pointer !important;
    width: 100% !important;
    transition: background-color 0.2s !important;
  }
  .hs-button:hover, input[type="submit"]:hover {
    background-color: hsl(207 82% 42%) !important;
  }
  .hs-error-msgs label, .hs-error-msg {
    color: hsl(0 84% 60%) !important;
    font-size: 0.75rem !important;
  }
  .hs-form-checkbox label, .hs-form-radio label,
  .hs-form-booleancheckbox label {
    color: #e2e8f0 !important;
    font-size: 0.875rem !important;
    font-weight: 400 !important;
  }
  .hs-form-checkbox input[type="checkbox"],
  .hs-form-booleancheckbox input[type="checkbox"] {
    accent-color: hsl(207 82% 47%) !important;
  }
  .hs-richtext, .hs-richtext p,
  .legal-consent-container .hs-form-booleancheckbox-display > span,
  .legal-consent-container p {
    color: hsl(215 15% 60%) !important;
    font-size: 0.75rem !important;
  }
  .hs-form-required { color: hsl(0 84% 60%) !important; }
  input[type="file"] { color: #e2e8f0 !important; font-size: 0.875rem !important; }
${HUBSPOT_THANK_YOU_CSS}
`;

/* Shared CSS for light-themed HubSpot forms (contact page, light sections) */
export const HUBSPOT_LIGHT_CSS = `
  body { background: transparent !important; }
  label, .hs-field-desc, legend {
    color: hsl(216 25% 16%) !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    font-family: 'Montserrat', sans-serif !important;
    margin-bottom: 0.375rem !important;
  }
  .hs-input, input[type="text"], input[type="email"], input[type="url"],
  input[type="tel"], input[type="number"], textarea, select {
    background-color: #fff !important;
    border: 1px solid hsl(210 20% 80%) !important;
    border-radius: 0.375rem !important;
    padding: 0.625rem 1rem !important;
    font-size: 0.875rem !important;
    color: hsl(216 25% 16%) !important;
    font-family: 'Montserrat', sans-serif !important;
    width: 100% !important;
  }
  .hs-input:focus, input:focus, textarea:focus, select:focus {
    outline: none !important;
    box-shadow: 0 0 0 2px hsl(207 82% 47% / 0.5) !important;
    border-color: hsl(207 82% 47%) !important;
  }
  .hs-input::placeholder, input::placeholder, textarea::placeholder {
    color: hsl(215 15% 50%) !important;
  }
  .hs-button, input[type="submit"] {
    background-color: hsl(207 82% 47%) !important;
    color: #fff !important;
    border: none !important;
    border-radius: 0.375rem !important;
    padding: 0.75rem 1.5rem !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    font-family: 'Montserrat', sans-serif !important;
    cursor: pointer !important;
    width: 100% !important;
    transition: background-color 0.2s !important;
  }
  .hs-button:hover, input[type="submit"]:hover {
    background-color: hsl(207 82% 42%) !important;
  }
  .hs-error-msgs label, .hs-error-msg {
    color: hsl(0 84% 60%) !important;
    font-size: 0.75rem !important;
  }
  .hs-form-checkbox label, .hs-form-radio label,
  .hs-form-booleancheckbox label {
    color: hsl(216 25% 25%) !important;
    font-size: 0.875rem !important;
    font-weight: 400 !important;
  }
  .hs-form-checkbox input[type="checkbox"],
  .hs-form-booleancheckbox input[type="checkbox"] {
    accent-color: hsl(207 82% 47%) !important;
  }
  .hs-richtext, .hs-richtext p,
  .legal-consent-container .hs-form-booleancheckbox-display > span,
  .legal-consent-container p {
    color: hsl(215 15% 40%) !important;
    font-size: 0.75rem !important;
  }
  .hs-form-required { color: hsl(0 84% 60%) !important; }
  input[type="file"] { color: hsl(216 25% 16%) !important; font-size: 0.875rem !important; }
${HUBSPOT_THANK_YOU_CSS}
`;
